import { Trans, t } from "@lingui/macro";
import type { SortOrder as GraphqlSortOrder } from "@src/__generated__/graphql";
import type {
  ProjectRevenueOrderByColumnEnum,
  SortOrder as UrqlSortOrder,
} from "@src/__generated__/urql-graphql";
import { ACTIONS_COLUMN_ID, DataTable } from "@src/components/ui-kit";
import { lowerCase } from "lodash";
import { observer } from "mobx-react-lite";
import { Fragment, useMemo } from "react";
import { ProfitAndRevenueAmount } from "../../../common/components";
import {
  createProjectColumns,
  createRevenueShareCol,
  withProjectHeaderTooltip,
} from "../../common";
import { ModalProjectReportDetail } from "../../common/DetailModal";
import type { ProjectReportColumn, ProjectReportRow } from "../../common/types";
import { ProjectProfitModel } from "../../profit/models";
import { useStore } from "../store";

export const Table = observer(function Table() {
  const store = useStore();
  const columns = useMemo<ProjectReportColumn<ProjectReportRow>[]>(() => {
    const cols = createProjectColumns<ProjectReportRow>({
      onDetailClick: store.reportStore.openDetailModal,
    });

    cols.push({
      id: "_ourWorkIncome",
      meta: { label: t`Work invoiced`, align: "right" },
      header: withProjectHeaderTooltip(
        t`Work invoiced`,
        <Trans>Invoiced amount of Work Budget Items (without VAT)</Trans>,
      ),
      cell: ({ row }) => {
        if (row.original instanceof ProjectProfitModel) {
          return null;
        }

        return (
          <ProfitAndRevenueAmount
            amount={row.original.our_work_billings_for_date_range}
          />
        );
      },
    });

    store.reportStore.expenseBudgetCategories.forEach((expCategory) => {
      cols.push({
        id: `billings_${expCategory.id}`,
        meta: {
          label: `${expCategory.name} ${t`Invoiced`}`,
          align: "right",
        },
        header: withProjectHeaderTooltip(
          `${expCategory.name} ${t`Invoiced`}`,
          t`Invoiced amount of ${lowerCase(expCategory.name)} Budget Items (without VAT)`,
        ),
        cell: ({ row }) => {
          if (row.original instanceof ProjectProfitModel) {
            return null;
          }

          const billings = row.original.expenses_billings_for_date_range.find(
            (category) =>
              category.expense_budget_category_id === expCategory.id,
          )?.billings;

          return <ProfitAndRevenueAmount amount={billings ?? 0} />;
        },
      });
    });

    cols.push(
      {
        id: "_ourGrossIncome",
        meta: { label: t`Gross Income`, align: "right" },
        header: withProjectHeaderTooltip(
          <Trans>Gross Income</Trans>,
          <Trans>
            Sum of Work Invoiced plus Production, Media and Royalties Profits.
          </Trans>,
        ),
        cell: ({ row }) => (
          <ProfitAndRevenueAmount
            amount={row.original.gross_income_for_date_range}
          />
        ),
      },
      {
        id: "total_invoiced",
        meta: { label: t`Total Invoiced`, align: "right" },
        header: withProjectHeaderTooltip(
          <Trans>Total Invoiced</Trans>,
          <Trans>
            Sum of the invoiced amount for Work, Media and Production
          </Trans>,
        ),
        cell: ({ row }) => {
          if (row.original instanceof ProjectProfitModel) {
            return null;
          }

          return (
            <ProfitAndRevenueAmount amount={row.original.total_invoiced} />
          );
        },
      },
      createRevenueShareCol(),
      {
        id: ACTIONS_COLUMN_ID,
      },
    );

    return cols;
  }, [
    store.reportStore.expenseBudgetCategories,
    store.reportStore.openDetailModal,
  ]);

  const orderedBy = store.reportStore.orderBy.clauses[0];

  return (
    <Fragment>
      <DataTable.Body<ProjectReportRow, ProjectRevenueOrderByColumnEnum>
        tableKey={store.key}
        data={store.projects as ProjectReportRow[]}
        columns={columns}
        orderedBy={
          orderedBy
            ? (() => {
                const order = orderedBy.order as GraphqlSortOrder | undefined;

                if (!order) {
                  return undefined;
                }

                return {
                  column: orderedBy.column,
                  order,
                };
              })()
            : undefined
        }
        onOrderByChange={(clause) => {
          if (!clause.order) {
            store.reportStore.orderBy.setClauses([]);
            return;
          }

          const order = clause.order as UrqlSortOrder | undefined;

          if (!order) {
            store.reportStore.orderBy.setClauses([]);
            return;
          }

          store.reportStore.orderBy.setClauses([
            { column: clause.column, order },
          ]);
        }}
      />
      <ModalProjectReportDetail store={store.reportStore} />
    </Fragment>
  );
});
